import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  Input,
  message,
  Spin,
  Typography,
  Table,
} from "../utils/antd-imports/AntdImports";
import { sendMessage } from "../utils/WebSocket";
import "./ChatMsgPad.css";
import { useDispatch, useSelector } from "react-redux";
import { containsPersonalInfo, formatToUSCurrency } from "../utils/Utils";
import { ReactComponent as ChatSendIcon } from "../../../assests/chat-send-icon.svg";
import {
  defaultTermsBookingStatus,
  getCampaignsForPurchase,
} from "../../../store/thunks/buyer-thunks/BuyerThunks";
import {
  CheckOutlined,
  CloseOutlined,
  SyncOutlined,
  TagOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { setIsProductInfo } from "../../../store/slices/common-slice/chat-communication/ChatCommunicationActionItems";
import ReusableModal from "../../modal/re-usable-modal/ReusableModal";
import {
  getChatHistory,
  updateAllNegotiationTerms,
} from "../../../store/thunks/common-thunks/commonThunk";
import {
  clearLiveMsgNotification,
  clearLiveMsgNotificationForProduct,
} from "../../../store/slices/common-slice/web-sockets/WebSocketMsgSlice";

const { Title } = Typography;
const ChatMsgPad = ({
  senderName,
  bookingId,
  currentUserId,
  receiverUserId,
  campaignId,
  productId,
  selectedChatProduct,
  hideTermsActions = false,
  onMakeTermsRequest,
  optimisticTermsMessage,
  isProductInfo,
  allProductsApproved,
  selectedProductApproved,
  selectedCampaign,
  onLatestMessageChange,
  scrollToLatestTrigger,
  updateSelectedChatProduct,
  // chatHistory, // ✅ history from store
}) => {
  const bottomRef = useRef(null);
  const Text = Typography;
  const [messagesByProduct, setMessagesByProduct] = useState({});
  const [inputValue, setInputValue] = useState("");
  const { isLiveMsg } = useSelector((state) => state.webSocketMsgs);
  const { chatLoading, productsChatHistory } = useSelector(
    (state) => state.chatHistory,
  );
  const dispatch = useDispatch();
  const productHistory =
    productsChatHistory?.find(
      (history) => String(history?.bookingId) === String(bookingId),
    ) || null;
  const activeProductId = String(
    productHistory?.productId ||
      selectedChatProduct?.originalProduct?.product_id ||
      productId ||
      selectedChatProduct?.originalProduct?._id ||
      selectedChatProduct?.id ||
      "",
  );
  const chatKey = `${campaignId || ""}-${bookingId || ""}-${activeProductId || ""}`;
  const messages = messagesByProduct[chatKey] || [];
  const userRole = useSelector((state) => state.login.userType);
  const { userId } = useSelector((state) => state.login);
  const { clientId } = useSelector((state) => state.userprofile);
  const [isActionModal, setIsActionModal] = useState(false);
  const [assetActionType, setAssetActionType] = useState(null);
  const [approveData, setApproveData] = useState(null);
  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);
  // Scroll to the latest message when View Thread is clicked
  useEffect(() => {
    if (scrollToLatestTrigger > 0) {
      bottomRef.current?.scrollIntoView({
        behavior: "smooth",
      });
    }
  }, [scrollToLatestTrigger]);
  let socketId = userRole === "basic" ? userId : clientId;
  useEffect(() => {
    if (!optimisticTermsMessage || !chatKey) {
      return;
    }
    const optimisticId = String(
      optimisticTermsMessage?.id || optimisticTermsMessage?.messageId || "",
    );
    if (!optimisticId) {
      return;
    }
    setMessagesByProduct((prev) => {
      const existing = prev[chatKey] || [];
      const alreadyExists = existing.some(
        (msg) =>
          String(msg?.id) === optimisticId ||
          String(msg?.messageId) === optimisticId,
      );
      if (alreadyExists) {
        return prev;
      }
      const newMessage = {
        ...optimisticTermsMessage,
        id: optimisticTermsMessage?.id || optimisticId,
        messageId: optimisticTermsMessage?.messageId || optimisticId,
        productId: activeProductId,
        campaignId: optimisticTermsMessage?.campaignId || campaignId,
        bookingId: optimisticTermsMessage?.bookingId || bookingId,
        terms_data:
          optimisticTermsMessage?.terms_data ||
          parseTermsMessage(optimisticTermsMessage?.text),

        isOptimistic: true,
      };

      return {
        ...prev,
        [chatKey]: [...existing, newMessage],
      };
    });
  }, [optimisticTermsMessage, chatKey, activeProductId, campaignId, bookingId]);
  // clearing live notification count
  useEffect(() => {
    if (!isLiveMsg?.message) return;
    const liveCampaignId = String(isLiveMsg?.campaignId || "");
    const liveBookingId = String(isLiveMsg?.bookingId || "");
    const selectedCampaignId = String(selectedCampaign?.id || "");
    const selectedBookingId = String(selectedChatProduct?.bookingId || "");
    const selectedProductCampaignId = String(
      selectedChatProduct?.campaignId || "",
    );
    if (!liveCampaignId || !liveBookingId) {
      return;
    }
    const isSameCampaign =
      liveCampaignId === selectedCampaignId ||
      liveCampaignId === selectedProductCampaignId;
    const isSameBooking = liveBookingId === selectedBookingId;
    if (!isSameCampaign || !isSameBooking) {
      return;
    }
    dispatch(
      clearLiveMsgNotificationForProduct({
        campaignId: liveCampaignId,
        productId: liveBookingId,
      }),
    );
  }, [
    isLiveMsg,
    selectedCampaign?.id,
    selectedChatProduct?.bookingId,
    selectedChatProduct?.campaignId,
    dispatch,
  ]);
  const latestHistoryMessage = useMemo(() => {
    if (!Array.isArray(productsChatHistory)) {
      return null;
    }
    const productHistory = productsChatHistory?.find(
      (item) =>
        String(item?.productId) === String(activeProductId) &&
        String(item?.bookingId) === String(bookingId),
    );
    if (!productHistory?.chatHistory?.length) {
      return null;
    }
    // Latest message is the last item
    return productHistory.chatHistory[productHistory.chatHistory.length - 1];
  }, [productsChatHistory, activeProductId, bookingId]);

  // Load history ONLY for the currently selected campaign/product
  useEffect(() => {
    if (!bookingId || !productHistory) {
      return;
    }
    // Make absolutely sure this history belongs
    // to the currently selected booking
    if (String(productHistory?.bookingId) !== String(bookingId)) {
      return;
    }
    const historyProductId = String(
      productHistory?.productId || productId || "",
    );
    const historyArray = Array.isArray(productHistory?.chatHistory)
      ? productHistory.chatHistory
      : [];
    const formattedHistory = historyArray?.map((msg) => ({
      id: msg?.messageId || msg?.id,
      messageId: msg?.messageId || msg?.id,
      text: msg?.message,
      sender: msg?.sender,
      createdAt: msg?.timestamp,
      productId: historyProductId,
      campaignId,
      bookingId,
      chat_group_id: msg?.chat_group_id || null,
      terms_data: msg?.terms_data || null,
    }));
    // IMPORTANT:
    // Replace the history for this exact campaign + booking + product.
    // Do not merge the previous product's messages.
    setMessagesByProduct((prev) => ({
      ...prev,
      [chatKey]: formattedHistory,
    }));
  }, [
    bookingId,
    campaignId,
    productId,
    chatKey,
    productHistory?.bookingId,
    productHistory?.productId,
    productHistory?.chatHistory,
  ]);

  const parseTermsMessage = (message) => {
    if (!message) return null;
    try {
      const data = JSON.parse(message);
      if (
        data?.request_type === "initial_terms_request" ||
        data?.request_type === "counter_terms_request"
      ) {
        return data;
      }
      return null;
    } catch {
      return null;
    }
  };
  const normalizeTermsData = (termsData) => {
    if (!termsData) return null;
    return Array.isArray(termsData) ? termsData?.[0] || null : termsData;
  };
  const getTermsDataFromMessage = (msg) => {
    const rawTermsData = msg?.terms_data || parseTermsMessage(msg?.text);
    return {
      raw: rawTermsData,
      normalized: normalizeTermsData(rawTermsData),
    };
  };
  const { isLiveMsgNotification } = useSelector((state) => state.webSocketMsgs);
  // Handle incoming live messages via WebSocket
  useEffect(() => {
    if (!isLiveMsg?.message) return;
    dispatch(clearLiveMsgNotification());
    const activeProductId = String(
      isLiveMsg?.productId ||
        productHistory?.productId ||
        selectedChatProduct?.productId ||
        productId ||
        "",
    );

    if (!activeProductId) return;
    const liveCampaignId = String(isLiveMsg?.campaignId || "");
    const activeCampaignId = String(campaignId || "");
    const liveBookingId = String(isLiveMsg?.bookingId || "");
    const activeBookingId = String(bookingId || "");
    const isCampaignRelevant = liveCampaignId === activeCampaignId;
    const isBookingRelevant =
      !liveBookingId || !activeBookingId || liveBookingId === activeBookingId;
    if (!isCampaignRelevant || !isBookingRelevant) {
      return;
    }
    // Use socketId consistently:
    // basic user => userId
    // non-basic user => clientId
    const ownSocketId = String(socketId || "");
    const senderType =
      String(isLiveMsg?.fromUserId) === ownSocketId ? "sender" : "receiver";
    const messageId =
      isLiveMsg?.messageId ||
      `${isLiveMsg?.timestamp}-${isLiveMsg?.fromUserId}`;
    setMessagesByProduct((prev) => {
      const existingMessages = prev[chatKey] || [];
      const isDuplicate = existingMessages.some(
        (msg) =>
          String(msg?.id) === String(messageId) ||
          String(msg?.messageId) === String(messageId),
      );
      if (isDuplicate) {
        return prev;
      }
      const newMessage = {
        id: messageId,
        messageId,
        text: isLiveMsg.message,
        sender: senderType,
        createdAt: isLiveMsg.timestamp,
        productId: activeProductId,
        campaignId: isLiveMsg?.campaignId,
        bookingId: isLiveMsg?.bookingId,
        chat_group_id: isLiveMsg?.chat_group_id || isLiveMsg?.group_id || null,
        terms_data: parseTermsMessage(isLiveMsg.message),
      };
      return {
        ...prev,
        [chatKey]: [...existingMessages, newMessage],
      };
    });
  }, [
    isLiveMsg,
    chatKey,
    productHistory?.productId,
    campaignId,
    bookingId,
    socketId,
    selectedChatProduct?.productId,
    productId,
  ]);
  // Handle sending a message
  const handleSend = () => {
    if (!inputValue.trim()) return;
    if (containsPersonalInfo(inputValue)) {
      message.warning("Sharing personal contact information is not allowed.");
      return;
    }
    const tempId = Date.now();
    const optimisticMsg = {
      id: tempId,
      text: inputValue,
      sender: "sender",
      createdAt: new Date().toISOString(),
      productId: activeProductId,
      campaignId,
      bookingId,
    };

    setMessagesByProduct((prev) => ({
      ...prev,
      [chatKey]: [...(prev[chatKey] || []), optimisticMsg],
    }));
    if (userRole === "basic") {
      sendMessage({
        fromUserId: socketId,
        toUserId: receiverUserId,
        campaignId,
        productId,
        bookingId,
        message: inputValue,
      });
    } else {
      sendMessage({
        fromUserId: socketId,
        toUserId: productHistory?.message_sender_id,
        campaignId,
        productId: productHistory?.productId,
        bookingId: productHistory?.bookingId,
        message: inputValue,
      });
    }

    setInputValue("");
  };

  const formatEpochToDateTime = (epochMs) => {
    const date = new Date(epochMs);
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
      date.getDate(),
    )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
      date.getSeconds(),
    )}`;
  };

  formatEpochToDateTime(1767702858031);
  const parseTimestamp = (ts) => {
    return new Date(formatEpochToDateTime(ts)?.replace(" ", "T"));
  };
  useEffect(() => {
    if (!isLiveMsg) return;
    const liveTermsData = parseTermsMessage(isLiveMsg.message);
    const liveCampaignId = String(isLiveMsg?.campaignId || "");
    const liveBookingId = String(isLiveMsg?.bookingId || "");
    const isTermsRequest = [
      "initial_terms_request",
      "counter_terms_request",
    ].includes(liveTermsData?.request_type);
    if (isTermsRequest) {
      dispatch(
        getChatHistory({
          campaignId: liveCampaignId,
          bookingId: liveBookingId,
        }),
      );
    }
  }, [isLiveMsg, dispatch]);
  const getDateLabel = (date) => {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    const isSameDay = (d1, d2) =>
      d1.getFullYear() === d2.getFullYear() &&
      d1.getMonth() === d2.getMonth() &&
      d1.getDate() === d2.getDate();

    if (isSameDay(date, today)) return "Today";
    if (isSameDay(date, yesterday)) return "Yesterday";

    return date.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateValue) => {
    const localDate = new Date(dateValue); // auto converts from UTC → local
    return localDate.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const groupMessagesByDate = (messages = []) => {
    const groups = {};
    messages.forEach((msg) => {
      const timestamp = msg?.terms_data?.length
        ? msg.terms_data[0]?.created_at
        : msg?.createdAt || msg?.timestamp;
      const dateObj = parseTimestamp(timestamp);
      const dateKey = dateObj.toDateString();
      if (!groups[dateKey]) {
        groups[dateKey] = {
          label: getDateLabel(dateObj),
          items: [],
        };
      }
      groups[dateKey].items.push({
        ...msg,
        _dateObj: dateObj,
      });
    });

    return Object.values(groups).sort(
      (a, b) => a.items[0]._dateObj - b.items[0]._dateObj,
    );
  };

  const groupedMessages = groupMessagesByDate(messages);
  const latestTermsMessage = [...messages].reverse().find((msg) => {
    const termsData = msg?.terms_data || parseTermsMessage(msg?.text);
    const requestType = Array.isArray(termsData)
      ? termsData?.[0]?.request_type
      : termsData?.request_type;
    return (
      requestType === "initial_terms_request" ||
      requestType === "counter_terms_request"
    );
  });

  const latestTermsMessageId =
    latestTermsMessage?.messageId || latestTermsMessage?.id;
  const handleStatusTerms = (status) => {
    const productKey = selectedChatProduct?.originalProduct?.productbookingid;
    const payload = {
      terms_status:
        selectedChatProduct?.originalProduct?.booking_terms_status === 2
          ? 1
          : 2,
      product_booking_id: productKey,
    };
    dispatch(defaultTermsBookingStatus(payload)).then((res) => {
      if (res?.payload?.status == 1) {
        const updatedBookingTermsStatus =
          selectedChatProduct?.originalProduct?.booking_terms_status === 2
            ? 1
            : 2;
        updateSelectedChatProduct(
          selectedChatProduct,
          updatedBookingTermsStatus,
        );
        dispatch(getCampaignsForPurchase());
        dispatch(
          getChatHistory({
            campaignId: String(campaignId),
            bookingId: String(bookingId),
          }),
        );
      }
    });
  };
  useEffect(() => {
    if (!messages?.length) {
      onLatestMessageChange?.(null);
      return;
    }
    const latest = messages[messages.length - 1];
    onLatestMessageChange?.(latest);
  }, [messages, onLatestMessageChange]);
  const getTermsApprovalStatus = (termsData) => {
    if (!termsData) return 0;
    const data = Array.isArray(termsData) ? termsData : [termsData];
    const status = data?.[0]?.approval_status;
    return Number(status ?? 0);
  };
  const sortedMessages = [...messages]?.sort((a, b) => {
    const dateA = new Date(a?.createdAt || 0).getTime();
    const dateB = new Date(b?.createdAt || 0).getTime();
    return dateA - dateB;
  });
  const lastTermsMessage = [...sortedMessages]?.reverse()?.find((msg) => {
    const { normalized } = getTermsDataFromMessage(msg);
    return ["initial_terms_request", "counter_terms_request"].includes(
      normalized?.request_type,
    );
  });
  const { raw: lastTermsData, normalized: normalizedLastTermsData } =
    getTermsDataFromMessage(lastTermsMessage);
  const lastRequestType = normalizedLastTermsData?.request_type || null;
  const hasTermsRequest = [
    "initial_terms_request",
    "counter_terms_request",
  ].includes(lastRequestType);
  const latestChatGroupId = lastTermsMessage?.chat_group_id || null;
  // Missing approval_status on a newly received live message
  // is treated as pending until API history provides the status.
  const lastTermsApprovalStatus =
    normalizedLastTermsData?.approval_status == null
      ? 0
      : Number(normalizedLastTermsData.approval_status);
  const isLastTermsRejected = lastTermsApprovalStatus === 2;
  const requestType = lastRequestType;
  const paymentTerms = Array.isArray(lastTermsData)
    ? {
        calendarData: lastTermsData?.map(
          (item) => item?.due_date || item?.payment_date || "",
        ),
        percentageData: lastTermsData.map(
          (item) =>
            item?.payment_percentage_custom ?? item?.payment_percentage ?? 0,
        ),
      }
    : {
        calendarData: lastTermsData?.calendar_data || [],
        percentageData: lastTermsData?.percentage_data || [],
      };
  const chatGroupId = Array.isArray(lastTermsData)
    ? lastTermsData?.[0]?.group_id
    : lastTermsData?.group_id;
  const handleCounterOfferStatus = (approvalStatus, chatGroupId) => {
    if (!chatGroupId) {
      console.warn("Something went wrong. Please try again.");
      return;
    }
    const payload = {
      chat_group_id: chatGroupId,
      approval_status: approvalStatus,
    };
    dispatch(updateAllNegotiationTerms(payload)).then((res) => {
      if (res?.payload?.status == 1) {
        dispatch(getCampaignsForPurchase());
        dispatch(
          getChatHistory({
            campaignId: String(campaignId),
            bookingId: String(bookingId),
          }),
        );
      }
    });
  };
  const [liveRequestType, setLiveRequestType] = useState(null);
  useEffect(() => {
    const latestRequestType = Array.isArray(lastTermsData)
      ? lastTermsData?.[0]?.request_type
      : lastTermsData?.request_type;

    setLiveRequestType(latestRequestType || null);
  }, [lastTermsData]);
  const currentRequestType = liveRequestType || requestType;
  const TermsRequestMessage = ({
    message,
    termsData: historyTermsData,
    sender,
    chatGroupId: messageChatGroupId,
    totalAmount = 0,
    isLatestCounterOffer = false,
    isApprovedChatHistory = false,
  }) => {
    const parsedTermsData = parseTermsMessage(message);
    const termsData = historyTermsData || parsedTermsData;
    if (!termsData) return null;
    let request_type;
    let calendar_data = [];
    let percentage_data = [];
    let comment_data = "";
    if (Array.isArray(termsData)) {
      request_type = termsData?.[0]?.request_type;
      calendar_data = termsData.map(
        (item) => item?.due_date || item?.payment_date || "",
      );
      percentage_data = termsData.map(
        (item) =>
          item?.payment_percentage_custom ?? item?.payment_percentage ?? 0,
      );
      comment_data = termsData?.[0]?.comment_data || "";
    } else {
      request_type = termsData?.request_type;
      calendar_data = termsData?.calendar_data || [];
      percentage_data = termsData?.percentage_data || [];
      comment_data = termsData?.comment_data || "";
    }
    const selectedPaymentTerms = {
      calendarData: calendar_data,
      percentageData: percentage_data,
    };
    const isCounterRequest = request_type === "counter_terms_request";
    const isReceivedCounterOffer = isCounterRequest && sender === "receiver";
    const dataSource = [
      ...calendar_data?.map((date, index) => {
        const percentage = Number(percentage_data[index] || 0);
        const amount = (totalAmount * percentage) / 100;

        return {
          key: `${date}-${index}`,
          installment: `Payment ${index + 1}`,
          dueDate: date || "-",
          amount,
          percentage,
          isTotal: false,
        };
      }),

      {
        key: "total",
        installment: "Total",
        dueDate: "-",
        amount: totalAmount,
        percentage: null,
        isTotal: true,
      },
    ];

    const columns = [
      {
        title: "Payment",
        dataIndex: "installment",
        key: "installment",
      },
      {
        title: "Due Date",
        dataIndex: "dueDate",
        key: "dueDate",
      },
      {
        title: "Amount",
        key: "amount",
        align: "right",
        render: (_, record) => (
          <>
            ${record?.amount.toLocaleString()}
            {!record?.isTotal && (
              <span className="terms-percentage"> ({record?.percentage}%)</span>
            )}
          </>
        ),
      },
    ];
    const getCounterTableClass = () => {
      if (isApprovedChatHistory) {
        return "approve-offer-table";
      }
      if (!isCounterRequest) {
        return "terms-chat-table";
      }
      // BASIC USER
      if (userRole === "basic") {
        if (sender === "sender") {
          return "counter-chat-table-seller";
        }

        if (sender === "receiver") {
          return "counter-chat-table";
        }
      }

      // NON-BASIC USER
      if (userRole !== "basic") {
        if (sender === "sender") {
          return "counter-chat-table-seller";
        }

        if (sender === "receiver") {
          return "counter-chat-table";
        }
      }

      return "terms-chat-table";
    };
    return (
      <div
        className={`terms-chat-card ${
          isApprovedChatHistory
            ? "agreed-latest-message"
            : isCounterRequest && !isReceivedCounterOffer
              ? "terms-chat-card-counter"
              : isReceivedCounterOffer
                ? "terms-chat-card-counter-reciever"
                : ""
        }`}
      >
        <div className="terms-chat-header">
          <div className="terms-chat-title">
            <span
              className={`terms-chat-icon ${
                isApprovedChatHistory
                  ? "terms-chat-approve-icon"
                  : isReceivedCounterOffer
                    ? "terms-chat-icon-counter-receiver"
                    : isCounterRequest
                      ? "terms-chat-icon-counter"
                      : ""
              }`}
            >
              {isCounterRequest ? (
                <SyncOutlined
                  style={{
                    color: isApprovedChatHistory
                      ? "#4ade80 "
                      : isCounterRequest && isReceivedCounterOffer
                        ? "#1447E6"
                        : "",
                  }}
                />
              ) : (
                <TagOutlined className="rotate-icon" />
              )}
            </span>

            <span
              className={`${
                isApprovedChatHistory
                  ? "approved-chat-counter"
                  : isReceivedCounterOffer
                    ? "counter-header-text"
                    : isCounterRequest
                      ? "counter-header-text-sender"
                      : ""
              }`}
            >
              {isCounterRequest
                ? isReceivedCounterOffer
                  ? userRole === "basic"
                    ? "COUNTER-TERMS FROM SELLER"
                    : "COUNTER-TERMS FROM BUYER"
                  : sender === "sender"
                    ? "YOUR COUNTER-TERMS"
                    : userRole === "basic"
                      ? "YOUR TERMS REQUEST"
                      : "BUYER TERMS"
                : userRole === "basic"
                  ? "YOUR TERMS REQUEST"
                  : "BUYER TERMS"}
            </span>
          </div>

          <span
            className={`terms-chat-status ${
              isApprovedChatHistory
                ? "asset-status-approved "
                : isReceivedCounterOffer
                  ? "terms-chat-reciver-counter common-action-items-text-red"
                  : isCounterRequest
                    ? "terms-chat-status-counter-sent common-action-items-text-red"
                    : ""
            }`}
          >
            {isApprovedChatHistory ? (
              <>
                Agreed <span>✓</span>
              </>
            ) : isReceivedCounterOffer ? (
              "Counter Received"
            ) : isCounterRequest ? (
              "Counter Sent"
            ) : (
              "Terms Requested"
            )}
          </span>
        </div>
        <div className="terms-chat-total">
          <span className="terms-chat-total-amount">
            ${totalAmount.toLocaleString()}
          </span>

          <span className="terms-chat-total-label">total</span>
        </div>
        {isCounterRequest ? (
          <div className="counter-offer-chat-content">
            <div className="counter-offer-chat-schedule">
              <Table
                className={getCounterTableClass()}
                columns={columns}
                dataSource={dataSource}
                pagination={false}
                bordered={false}
                size="small"
              />
            </div>
            {isReceivedCounterOffer && (
              <>
                <div className="counter-offer-chat-actions">
                  <Button
                    className={`counter-offer-chat-accept ${
                      selectedProductApproved ||
                      !isLatestCounterOffer ||
                      isApprovedChatHistory ||
                      selectedChatProduct?.originalProduct
                        ?.booking_terms_status == 2 ||
                      lastTermsMessage?.terms_data[0]?.approval_status === 2
                        ? "disabled-opacity"
                        : ""
                    }`}
                    onClick={() => {
                      const chatGroupId = lastTermsMessage?.chat_group_id;
                      handleCounterOfferStatus(1, chatGroupId);
                    }}
                    disabled={
                      selectedProductApproved ||
                      !isLatestCounterOffer ||
                      isApprovedChatHistory ||
                      selectedChatProduct?.originalProduct
                        ?.booking_terms_status == 2 ||
                      lastTermsMessage?.terms_data[0]?.approval_status === 2
                    }
                  >
                    <span>✓</span>
                    Accept This
                  </Button>
                  <Button
                    className={`counter-offer-chat-counter ${
                      selectedProductApproved ||
                      !isLatestCounterOffer ||
                      isApprovedChatHistory ||
                      selectedChatProduct?.originalProduct
                        ?.booking_terms_status == 2 ||
                      lastTermsMessage?.terms_data[0]?.approval_status === 2
                        ? "disabled-opacity"
                        : ""
                    }`}
                    onClick={() => {
                      onMakeTermsRequest?.({
                        product: selectedChatProduct,
                        campaign: selectedCampaign,
                        paymentTerms: selectedPaymentTerms,
                        termsType: "custom",
                      });
                    }}
                    disabled={
                      selectedProductApproved ||
                      !isLatestCounterOffer ||
                      selectedChatProduct?.originalProduct
                        ?.booking_terms_status == 2 ||
                      lastTermsMessage?.terms_data[0]?.approval_status === 2
                    }
                  >
                    <SyncOutlined />
                    Counter Back
                  </Button>
                </div>
              </>
            )}
          </div>
        ) : (
          <Table
            className={`${
              isCounterRequest && userRole !== "basic"
                ? "counter-chat-table"
                : "terms-chat-table"
            }`}
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            bordered={false}
            size="small"
          />
        )}
        {comment_data?.trim() && (
          <div className="terms-chat-comment ">
            <div className="terms-chat-comment-label ">Note</div>
            <div className="terms-chat-comment-text">{comment_data}</div>
          </div>
        )}
      </div>
    );
  };
  const canAcceptBuyerTerms =
    userRole !== "basic" &&
    hideTermsActions &&
    !!lastTermsMessage &&
    lastTermsMessage?.sender === "receiver" &&
    hasTermsRequest &&
    lastTermsApprovalStatus === 0;
  const counterSellersButton =
    userRole === "basic" &&
    !!lastTermsMessage &&
    lastTermsMessage?.sender === "receiver" &&
    lastRequestType === "counter_terms_request" &&
    lastTermsApprovalStatus === 0;
  return (
    <>
      <Title level={5}>{senderName}</Title>
      <div
        className={`chat-pad-container ${hideTermsActions ? "chat-pad-container-all" : ""}`}
      >
        {chatLoading ? (
          <div
            style={{
              display: "flex",
              alignContent: "center",
              justifyContent: "center",
            }}
          >
            <Spin style={{ color: "#d2042d" }} />
          </div>
        ) : (
          <>
            <div className="re-designed-inchat-conatiner">
              <div className="seller-intial-chat-div">
                <Text className="common-sub-header-black-14">
                  {userRole !== "basic" ? "Buyer" : "Seller"}
                </Text>
                {hideTermsActions ? (
                  <span className="common-text-1E2939-12">
                    Hi! Happy to discuss{" "}
                    <span>{selectedChatProduct?.title}</span> (
                    <span>{selectedChatProduct?.productId}</span>
                    ). What would you like to adjust?
                  </span>
                ) : (
                  <span className="common-text-1E2939-12">
                    Hi! Happy to discuss{" "}
                    <span>{selectedChatProduct?.asset}</span> (
                    <span>{selectedChatProduct?.assetId}</span>
                    ). What would you like to adjust?
                  </span>
                )}
              </div>
              {groupedMessages?.map((group) => (
                <div key={group.label}>
                  {/* Date divider */}
                  <div
                    style={{
                      textAlign: "center",
                      margin: "16px 0",
                      fontSize: 13,
                      color: "#6b6b6b",
                      fontWeight: 500,
                    }}
                  >
                    <span
                      style={{
                        background: "#EDEDED",
                        padding: "4px 12px",
                        borderRadius: 12,
                      }}
                    >
                      {group?.label}
                    </span>
                  </div>
                  {/* Messages */}
                  {group?.items?.map((msg) => {
                    const msgTermsData =
                      msg?.terms_data || parseTermsMessage(msg?.text);
                    const approvalStatus = getTermsApprovalStatus(msgTermsData);
                    const isApprovedChatHistory = approvalStatus === 1;
                    const isRejectedChatHistory = approvalStatus === 2;
                    return (
                      <div key={msg?.id} style={{ marginBottom: "16px" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent:
                              msg?.sender === "sender"
                                ? "flex-end"
                                : "flex-start",
                            marginBottom: 6,
                          }}
                        >
                          {msg?.terms_data || parseTermsMessage(msg?.text) ? (
                            <TermsRequestMessage
                              message={msg?.text}
                              termsData={msg?.terms_data}
                              sender={msg?.sender}
                              chatGroupId={msg?.chat_group_id}
                              totalAmount={
                                Number(
                                  selectedChatProduct?.originalProduct
                                    ?.offerpriceperselectedDates,
                                ) || 0
                              }
                              isLatestCounterOffer={
                                String(msg?.messageId || msg?.id) ===
                                String(latestTermsMessageId)
                              }
                              isApprovedChatHistory={isApprovedChatHistory}
                            />
                          ) : (
                            <div
                              className={
                                isApprovedChatHistory
                                  ? "agreed-latest-message"
                                  : ""
                              }
                              style={{
                                backgroundColor:
                                  msg?.sender === "sender"
                                    ? "#d2042d"
                                    : "#F0F0F0",
                                color:
                                  msg?.sender === "sender"
                                    ? "#FFFFFF"
                                    : "#0A0F0D",
                                padding: "14px",
                                fontWeight: 400,
                                fontSize: 16,
                                fontFamily: "Roboto",
                                borderRadius: 8,
                                maxWidth: "70%",
                                wordBreak: "break-word",
                              }}
                            >
                              {msg?.text}
                            </div>
                          )}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            justifyContent:
                              msg?.sender === "sender"
                                ? "flex-end"
                                : "flex-start",
                            fontSize: 14,
                            marginTop: 4,
                            color: "#5B5E5D",
                            fontFamily: "Roboto",
                            fontWeight: 400,
                          }}
                        >
                          {formatTime(msg?._dateObj)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))}

              {selectedChatProduct?.statusType === "agreed" && (
                <div className="offer-accepted-message">
                  <Text style={{ color: "#047857" }}>
                    {" "}
                    🎉 Terms accepted! We've agreed on{" "}
                    <strong>
                      {formatToUSCurrency(
                        selectedChatProduct?.originalProduct
                          ?.offerpriceperselectedDates,
                      )}
                    </strong>{" "}
                    .We'll prepare the Insertion Order shortly.
                  </Text>
                </div>
              )}
              {getTermsApprovalStatus(lastTermsData) === 2 && (
                <div className="offer-declined-message">
                  <CloseOutlined className="declined-icon" />

                  <span>
                    {userRole === "basic" ? (
                      <>
                        The seller has declined this offer. Feel free to send a
                        revised offer or continue the conversation.
                      </>
                    ) : (
                      <>
                        You have declined this offer. The buyer can send a
                        revised offer or continue the conversation.
                      </>
                    )}
                  </span>
                </div>
              )}
            </div>
            {!hideTermsActions && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  marginTop: "10px",
                }}
              >
                <Button
                  className={`common-button-green ${
                    selectedProductApproved ||
                    selectedChatProduct?.originalProduct
                      ?.booking_terms_status == 2
                      ? "disabled-opacity"
                      : ""
                  }`}
                  style={{ width: "45%" }}
                  onClick={() => handleStatusTerms("approve")}
                  disabled={
                    selectedProductApproved ||
                    selectedChatProduct?.originalProduct
                      ?.booking_terms_status == 2
                  }
                >
                  <span className="common-text-008236-12">
                    {selectedProductApproved ||
                    selectedChatProduct?.originalProduct
                      ?.booking_terms_status == 2
                      ? "Accepted Terms"
                      : "Accept Terms"}
                  </span>
                </Button>
                <Button
                  className={`common-button-red ${
                    !selectedChatProduct?.originalProduct
                      ?.booking_terms_status ||
                    selectedChatProduct?.originalProduct
                      ?.booking_terms_status === 1 ||
                    selectedChatProduct?.originalProduct
                      ?.booking_terms_status == 3
                      ? "disabled-opacity"
                      : ""
                  }`}
                  style={{ width: "45%" }}
                  onClick={() => handleStatusTerms("remove")}
                  disabled={
                    !selectedChatProduct?.originalProduct
                      ?.booking_terms_status ||
                    selectedChatProduct?.originalProduct
                      ?.booking_terms_status === 1 ||
                    selectedChatProduct?.originalProduct
                      ?.booking_terms_status == 3
                  }
                >
                  <span className="common-text-white-14">Remove</span>
                </Button>
              </div>
            )}
          </>
        )}
        <div ref={bottomRef} />
      </div>
      {hideTermsActions && (
        <div className="messages-bottom-actions">
          {canAcceptBuyerTerms && (
            <Button
              className={`common-offer-action-button common-offer-action-button-success border-styles ${selectedProductApproved || lastTermsMessage?.sender === "sender" ? "disabled-opacity" : ""}`}
              icon={<CheckOutlined style={{ width: "12px" }} />}
              onClick={() => {
                setIsActionModal(true);
                setAssetActionType("approve");
                setApproveData({
                  product: selectedChatProduct,
                  campaign: selectedCampaign,
                  paymentTerms,
                });
              }}
              disabled={
                selectedProductApproved || lastTermsMessage?.sender === "sender"
              }
            >
              Accept Buyer Terms
            </Button>
          )}
          {(userRole === "basic" ||
            selectedChatProduct?.apply_terms_status == 1 ||
            hasTermsRequest) && (
            <Button
              className={`common-button-red common-button-red-28 common-button-radius ${
                allProductsApproved ||
                selectedProductApproved ||
                (userRole !== "basic" &&
                  lastTermsData?.[0]?.approval_status === 2)
                  ? "disabled-opacity"
                  : ""
              }`}
              icon={
                userRole === "basic" ? (
                  <ThunderboltOutlined
                    style={{ color: "#fff", width: "12px" }}
                  />
                ) : (
                  <SyncOutlined style={{ color: "#fff", width: "12px" }} />
                )
              }
              onClick={() => {
                const counterOfferData = {
                  product: selectedChatProduct,
                  campaign: selectedCampaign,
                  paymentTerms,
                };

                onMakeTermsRequest?.(counterOfferData);
              }}
              disabled={
                allProductsApproved ||
                selectedProductApproved ||
                (userRole !== "basic" &&
                  lastTermsData?.[0]?.approval_status === 2)
              }
            >
              <span className="common-text-FFFFFF-12">
                {userRole === "basic"
                  ? "Make a Terms Request"
                  : "Send Counter-Request"}
              </span>
            </Button>
          )}
          {counterSellersButton && (
            <Button
              className={`counter-offer-chat-counter common-button-radius baground-color text-bold-style ${selectedProductApproved ? "disabled-opacity" : ""}`}
              onClick={() => {
                onMakeTermsRequest?.({
                  product: selectedChatProduct,
                  campaign: selectedCampaign,
                  paymentTerms,
                  termsType: "custom",
                });
              }}
              disabled={selectedProductApproved}
            >
              <SyncOutlined style={{ marginBottom: "2px" }} />
              Counter Seller's Terms
            </Button>
          )}
          {canAcceptBuyerTerms && (
            <Button
              className={`common-offer-action-button common-offer-action-button-danger border-styles ${selectedProductApproved || isLastTermsRejected || lastTermsMessage?.sender === "sender" ? "disabled-opacity" : ""}`}
              icon={<CloseOutlined style={{ width: "12px", height: "auto" }} />}
              onClick={() => {
                const chatGroupId = lastTermsMessage?.chat_group_id;
                handleCounterOfferStatus(2, chatGroupId);
              }}
              disabled={
                selectedProductApproved ||
                isLastTermsRejected ||
                lastTermsMessage?.sender === "sender"
              }
            >
              Decline
            </Button>
          )}
        </div>
      )}
      <div className="chat-box-input-filed chat-input-container">
        <Input
          className="chat-message-input common-text-0A0A0A80-12"
          placeholder="Type a message..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onPressEnter={handleSend}
          bordered={false}
        />
        <ChatSendIcon style={{ cursor: "pointer" }} onClick={handleSend} />
      </div>
      <ReusableModal
        type={assetActionType}
        approveData={approveData}
        isActionModal={isActionModal}
        setIsActionModal={setIsActionModal}
        lastTermsData={lastTermsData}
        chatGroupId={latestChatGroupId}
        lastTermsMessage={lastTermsMessage}
      />
    </>
  );
};

export default ChatMsgPad;
